/**
 * Scene 4: configuration.
 *
 * Model:
 *   - The BASE VALUE (first forward stage) is set ONCE and is inherited by
 *     every following stage. It is the only required field (used for the
 *     progress count).
 *   - For each additional stage the user can DELIBERATELY record a deviation
 *     via "+". Without a deviation the stage silently inherits the base
 *     value (no overlay written to git, see buildConfigPayload({stage})).
 *
 * UI:
 *   - Base value: a compact field, click opens an overlay editor in the cell.
 *   - Additional stage: "Inherits: <value>" with a "+" hint; a deviation
 *     shows a warning field with a reset option. While editing, a spotlight
 *     dims the other cells, but the columns never shift (the overlay is
 *     positioned absolutely).
 *
 * Single-stage space (source plus target only, i.e. one forward stage): shows
 * exactly one base-value column, so it still reads as "Parameter | Source |
 * Target | Status" with no visual regression.
 *
 * The data model (entry.newValue = base, entry.byStage[stage] = overlay) is
 * unchanged; reducer PATCH_CFG_ENTRY (with or without stage) and the snapshot
 * contract (buildWerkConfigsPerStage) are identical to before.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "../i18n";
import { useTransportV2, hasWerke, buckets, vorwaertsStufen } from "../context/TransportV2Context";
import { useArtifactConfigs } from "../hooks/useArtifactConfigs";
import { useGitConfigs } from "../hooks/useGitConfigs";
import { classifyConfigStatus } from "../../../utils/transport-config-helpers";

function bucketByGroup(entries) {
  const sender = [], receiver = [], other = [];
  for (const e of entries || []) {
    if (!e) continue;
    if (e.group === "sender") sender.push(e);
    else if (e.group === "receiver") receiver.push(e);
    else other.push(e);
  }
  return { sender, receiver, other };
}

/**
 * Counts required/open fields on the BASE stage (the entry's root) only.
 * Later stages are optional overlays and are never reported as "open",
 * otherwise the progress bar would drop to 0 for every overlay field.
 */
function countMissing(entries) {
  let miss = 0, total = 0;
  for (const e of entries || []) {
    if (!e) continue;
    total++;
    const st = classifyConfigStatus(e);
    if (st === "required" || st === "empty") miss++;
  }
  return { miss, total };
}

/**
 * The effective base value of an entry: the set newValue, otherwise, for
 * non-sender/receiver parameters, the source default (which would apply at
 * transport time anyway). For sender/receiver parameters without a value it
 * stays empty, since those are genuinely required.
 */
function effektiverBasisWert(entry) {
  if (!entry) return "";
  if (entry.newValue) return entry.newValue;
  const isSR = entry.group === "sender" || entry.group === "receiver";
  return isSR ? "" : (entry.defaultValue || "");
}

/**
 * The value a later stage inherits: the nearest earlier stage with its own
 * deviation, otherwise the effective base value. Also returns the stage code
 * the value came from.
 */
function geerbterWert(entry, stufen, sidx) {
  for (let i = sidx - 1; i >= 1; i--) {
    const s = stufen[i];
    const v = entry?.byStage?.[s.key]?.newValue;
    if (v) return { value: v, code: s.code };
  }
  const basis = stufen[0];
  return { value: effektiverBasisWert(entry), code: basis?.code || "" };
}

/**
 * Does the entry have a real deviation on any additional stage (a value set
 * that differs from the inherited value)?
 */
function hatAbweichung(entry, stufen) {
  for (let i = 1; i < stufen.length; i++) {
    const ov = entry?.byStage?.[stufen[i].key]?.newValue;
    if (!ov) continue;
    if (ov !== geerbterWert(entry, stufen, i).value) return true;
  }
  return false;
}

const EDIT_GLYPH = (
  <svg className="edit-glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
    <path d="m18.5 2.5 3 3-9 9H9v-3z" />
  </svg>
);

export default function Scene4Config() {
  const { t } = useLang();
  const { state, dispatch } = useTransportV2();
  const { loadFor } = useArtifactConfigs();
  const gitCfgs = useGitConfigs();
  const [openAcc, setOpenAcc] = useState({});
  const [showWarn, setShowWarn] = useState(false);

  // Overlay editor. editing = { artId, paramKey, stageKey, isBasis, stageCode,
  // sourceDefault, inheritedValue, inheritedCode, hasOverride }. editVal holds
  // the current input.
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const editValRef = useRef("");
  const editingRef = useRef(null);
  const editorRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => { editValRef.current = editVal; }, [editVal]);
  useEffect(() => { editingRef.current = editing; }, [editing]);

  const bucketList = buckets(state);
  const withWerke = hasWerke(state);
  const tab = state.cfgTab || bucketList[0]?.key || null;

  const stufen = useMemo(() => vorwaertsStufen(state), [state.space]); // eslint-disable-line react-hooks/exhaustive-deps
  const basisStage = stufen[0] || null;
  // Stable identity, so openEditor (useCallback) does not get rebound on
  // every render.
  const effektiveStufen = useMemo(() => (stufen.length ? stufen : (basisStage ? [basisStage] : [])), [stufen, basisStage]);
  const stageCount = effektiveStufen.length || 1;

  // Source stage code for the column header "Source (<code>)".
  const quellCode = useMemo(() => {
    const s = state.space;
    if (!s || !Array.isArray(s.stages) || !s.quellStufe) return "";
    return s.stages.find((st) => st.stageKey === s.quellStufe)?.code || "";
  }, [state.space]);

  useEffect(() => {
    if (!state.cfgTab && bucketList[0]) dispatch({ type: "SET_CFG_TAB", payload: bucketList[0].key });
  }, [state.cfgTab, bucketList, dispatch]);

  const artefakteInBucket = useCallback((werkKey) => {
    const list = [];
    for (const [origId, sel] of Object.entries(state.sel || {})) {
      if (!sel || !sel[werkKey]) continue;
      const precheckKey = Object.keys(state.precheckByBucket || {}).find((k) => {
        const e = state.precheckByBucket[k];
        return e?.werk?.kennung === (werkKey === "std" ? undefined : werkKey);
      }) || Object.keys(state.precheckByBucket || {})[0];
      const entry = state.precheckByBucket?.[precheckKey];
      const item = entry?.comparison?.find((a) => (a.originalId || a.Id) === origId);
      if (!item) continue;
      list.push({
        id: origId,
        name: item.Name || origId,
        suffixedId: item.suffixedId || item.Id,
        status: String(item.status || "").toLowerCase(),
      });
    }
    return list;
  }, [state.sel, state.precheckByBucket]);

  // Fetch the values maintained in Git FIRST. Only then the tenant values - the reducer layers
  // the Git values on top when merging, and the configs are cached after the first load. The
  // other way round the user would see the tenant value, adopt it, and the maintained Git state
  // would be silently overwritten on the snapshot.
  useEffect(() => {
    if (state.gitCfgState === "idle") gitCfgs.laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.gitCfgState, state.spaceId, state.pkgId]);

  useEffect(() => {
    if (!state.sourceInstanceId) return;
    // "fehler" here means: no CI/CD project, or no snapshot yet. Then there is nothing to
    // preload and the view falls back to the tenant values - but it still has to wait, otherwise
    // the ordering breaks.
    if (!["da", "leer", "fehler"].includes(state.gitCfgState)) return;
    const targetInst = state.space?.qasInstance;
    if (!targetInst) return;
    for (const b of bucketList) {
      const arts = artefakteInBucket(b.key);
      for (const a of arts) {
        const existing = state.cfg?.[b.key]?.[a.id];
        if (existing) continue;
        loadFor({
          sourceInstanceId: state.sourceInstanceId,
          targetInstanceId: targetInst.id,
          sourceArtifactId: a.id,
          suffixedId: a.suffixedId,
          werkKey: b.key,
          werkKennung: b.key === "std" ? "" : b.kennung || "",
        }).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sel, state.sourceInstanceId, state.gitCfgState, bucketList.length]);

  const counts = useCallback((werkKey) => {
    let miss = 0, total = 0;
    for (const a of artefakteInBucket(werkKey)) {
      const entries = state.cfg?.[werkKey]?.[a.id]?.entries;
      const c = countMissing(entries);
      miss += c.miss;
      total += c.total;
    }
    return { filled: total - miss, total };
  }, [artefakteInBucket, state.cfg]);

  const activeCounts = useMemo(() => {
    let miss = 0, total = 0;
    for (const b of bucketList) {
      const c = counts(b.key);
      miss += (c.total - c.filled);
      total += c.total;
    }
    return { filled: total - miss, total };
  }, [bucketList, counts]);

  const canNext = activeCounts.total === 0 || activeCounts.filled >= activeCounts.total;
  const progressPct = activeCounts.total ? Math.round(activeCounts.filled / activeCounts.total * 100) : 100;
  const offen = Math.max(0, activeCounts.total - activeCounts.filled);
  const onWeiter = () => {
    if (offen > 0) { setShowWarn(true); return; }
    dispatch({ type: "SET_STEP", payload: 5 });
  };
  const bestaetigeWeiter = () => {
    setShowWarn(false);
    dispatch({ type: "SET_STEP", payload: 5 });
  };

  const activeBucket = bucketList.find((b) => b.key === tab);
  const arts = tab ? artefakteInBucket(tab) : [];
  const targetInst = state.space?.qasInstance;

  // Patches
  const onPatchBasis = (werkKey, sourceId, paramKey, value) => {
    dispatch({
      type: "PATCH_CFG_ENTRY",
      payload: { werkKey, sourceId, paramKey, patch: { newValue: value, modified: true, fromTarget: false, fromGit: false } },
    });
  };
  const onPatchStage = (werkKey, sourceId, paramKey, stageKey, value) => {
    dispatch({
      type: "PATCH_CFG_ENTRY",
      payload: { werkKey, sourceId, paramKey, stage: stageKey, patch: { newValue: value, modified: true, fromTarget: false, fromGit: false } },
    });
  };

  // "Apply all suggested values" fills ONLY the base stage.
  const onFillAll = () => {
    for (const b of bucketList) {
      for (const a of artefakteInBucket(b.key)) {
        const entries = state.cfg?.[b.key]?.[a.id]?.entries || [];
        for (const e of entries) {
          if (!e || e.newValue) continue;
          const patch = { newValue: e.targetValue || e.defaultValue || "", modified: true };
          if (patch.newValue) dispatch({ type: "PATCH_CFG_ENTRY", payload: { werkKey: b.key, sourceId: a.id, paramKey: e.ParameterKey, patch } });
        }
      }
    }
  };

  // Overlay editor
  const openEditor = useCallback((artId, entry, st, sidx) => {
    const isBasis = sidx === 0;
    let inheritedValue = "", inheritedCode = "", hasOverride = false, cur = "", origValue = "";
    if (isBasis) {
      // Pre-fill ONLY the current value (empty stays empty). The source
      // default is shown as a placeholder in the field, so simply opening
      // and clicking away commits nothing.
      cur = entry.newValue || "";
      origValue = entry.newValue || "";
    } else {
      const inh = geerbterWert(entry, effektiveStufen, sidx);
      inheritedValue = inh.value;
      inheritedCode = inh.code;
      const ov = entry?.byStage?.[st.key]?.newValue || "";
      hasOverride = !!ov && ov !== inheritedValue;
      cur = ov || inheritedValue;
      origValue = ov; // the actually stored deviation ("" = none)
    }
    setEditing({
      artId, paramKey: entry.ParameterKey, stageKey: st.key, isBasis,
      stageCode: st.code, sourceDefault: entry.defaultValue || "",
      inheritedValue, inheritedCode, hasOverride, origValue,
    });
    setEditVal(cur);
  }, [effektiveStufen]);

  const closeEditor = useCallback(() => {
    setEditing(null);
    setEditVal("");
  }, []);

  const commitEditor = useCallback(() => {
    const ed = editingRef.current;
    if (!ed) { closeEditor(); return; }
    const v = editValRef.current;
    if (ed.isBasis) {
      // Only write if the value actually changed. Otherwise merely viewing
      // it (open plus click away) would flip fromTarget/status.
      if (v !== ed.origValue) onPatchBasis(tab, ed.artId, ed.paramKey, v);
    } else {
      // Empty or identical to the inherited value means no deviation. Only
      // write if the overlay state actually changes.
      const desired = (!v || v === ed.inheritedValue) ? "" : v;
      if (desired !== ed.origValue) onPatchStage(tab, ed.artId, ed.paramKey, ed.stageKey, desired);
    }
    closeEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, closeEditor]);

  const resetOverride = useCallback((artId, paramKey, stageKey) => {
    onPatchStage(tab, artId, paramKey, stageKey, "");
    closeEditor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, closeEditor]);

  // Clicking outside commits (no data loss); Escape discards, Enter commits.
  useEffect(() => {
    if (!editing) return;
    const onDown = (ev) => {
      if (editorRef.current && !editorRef.current.contains(ev.target)) commitEditor();
    };
    const id = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
    return () => { clearTimeout(id); document.removeEventListener("mousedown", onDown); };
  }, [editing, commitEditor]);

  // Close the editor on tab or package change.
  useEffect(() => { closeEditor(); }, [tab, closeEditor]);

  useEffect(() => {
    if (editing && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      const n = el.value.length;
      try { el.setSelectionRange(n, n); } catch { /* type=text ok */ }
    }
  }, [editing]);

  const computeRowStatus = (entry, isEditingRow) => {
    if (isEditingRow) return { cls: "idle", label: t("s4.rowEditing") };
    const base = classifyConfigStatus(entry);
    if (base === "required") return { cls: "crit", label: t("s4.rowBaseMissing") };
    if (base === "empty") return { cls: "idle", label: t("s4.rowOpen") };
    if (hatAbweichung(entry, effektiveStufen)) return { cls: "warn", label: t("s4.rowDiverges") };
    // Preloaded from Git: the maintained state. Ranked before "from target" because it beats
    // it - without the distinction a maintained value would look hand-typed.
    if (entry?.fromGit) return { cls: "info", label: t("s4.rowFromGit") };
    if (base === "fromTarget") return { cls: "info", label: t("s4.rowFromTarget") };
    return { cls: "ok", label: t("s4.rowSet") };
  };

  const renderEditor = (entry, st, sidx) => {
    const isBasis = sidx === 0;
    const ed = editing;
    const aria = isBasis
      ? t("s4.edAriaBasis", { param: entry.ParameterKey })
      : t("s4.edAriaOverride", { param: entry.ParameterKey, stage: st.code });
    const hint = isBasis
      ? t("s4.edSourceHint", { v: entry.defaultValue || "-" })
      : t("s4.edInheritHint", { stage: ed.inheritedCode || basisStage?.code || "", v: ed.inheritedValue || "-" });
    const placeholder = isBasis ? (entry.defaultValue || "") : (ed.inheritedValue || "");
    // The editor for the last (rightmost) stage opens to the left so it does
    // not run past the right edge; the others open to the right, closer to
    // the field.
    const toLeft = sidx > 0 && sidx === effektiveStufen.length - 1;
    return (
      <div
        ref={editorRef}
        className={"cell-editor" + (toLeft ? " to-left" : "")}
        role="group"
        aria-label={aria}
        onBlur={(e) => { if (editorRef.current && !editorRef.current.contains(e.relatedTarget)) commitEditor(); }}
      >
        <div className="ce-lbl">
          <span className="code">{st.code}</span>
          <span>{isBasis ? t("s4.hdrBasis") : t("s4.addOverride")}</span>
          <span className="inh" title={hint}>{hint}</span>
        </div>
        <input
          ref={inputRef}
          className="ce-txt"
          type="text"
          value={editVal}
          placeholder={placeholder}
          onChange={(e) => setEditVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { e.preventDefault(); closeEditor(); }
            if (e.key === "Enter") { e.preventDefault(); commitEditor(); }
          }}
        />
        <div className="ce-foot">
          {!isBasis && ed.hasOverride && (
            <button type="button" className="ce-btn dngr" onClick={() => resetOverride(ed.artId, ed.paramKey, ed.stageKey)}>
              {t("s4.edRemove")}
            </button>
          )}
          <button type="button" className="ce-btn ghost" onClick={closeEditor}>{t("s4.edCancel")}</button>
          <button type="button" className="ce-btn pri" onClick={commitEditor}>{t("s4.edApply")}</button>
        </div>
      </div>
    );
  };

  return (
    <section className="scene active scene-enter" id="s4">
      <div className="sh">
        <h2>{t("s4.h2")}</h2>
        <p>{t("s4.p")}</p>
      </div>

      <div className="cfg-head">
        <div className="progress-wrap">
          <div className="progress-label">
            <span>
              {t("s4.progress", { x: activeCounts.filled, y: activeCounts.total })}
              {withWerke && activeBucket ? t("s4.activeTab", { name: activeBucket.name }) : ""}
            </span>
            <span>{activeCounts.total ? progressPct + "%" : "-"}</span>
          </div>
          <div className="progress-track">
            <div className={"progress-fill" + (progressPct >= 100 && activeCounts.total ? " full" : "")} style={{ width: progressPct + "%" }} />
          </div>
        </div>
        <button className="btn btn-sec" onClick={onFillAll}>{t("s4.fillAll")}</button>
      </div>

      {targetInst && (
        <div className="inst-line">
          <span className="st st--ok"><i className="dot" />{t("s4.connected")}</span>
          {targetInst.name}
          <span className="muted" style={{ fontWeight: 500 }}>
            · {t("s4.targetInst")}{withWerke ? ` · ${bucketList.length} ${t("rt.werke")}` : ""}
            {stufen.length > 1 ? ` · ${t("s4.stagesN", { n: stufen.length })}` : ""}
          </span>
        </div>
      )}

      <div className="utabs">
        {bucketList.map((b) => {
          const c = counts(b.key);
          const cls = c.filled === c.total ? "fill-ok" : "fill-miss";
          return (
            <button
              key={b.key}
              className={"utab" + (tab === b.key ? " on" : "")}
              onClick={() => dispatch({ type: "SET_CFG_TAB", payload: b.key })}
            >
              {b.name}
              {b.kennung && <> <span className="ken">{b.kennung}</span></>}
              {" · "}
              <span className={cls}>{c.filled}/{c.total}</span>
            </button>
          );
        })}
      </div>

      <div>
        {arts.length === 0 && <div className="cred-box">{t("s4.noneWerk")}</div>}
        {arts.map((a, idx) => {
          const entries = state.cfg?.[tab]?.[a.id]?.entries || [];
          const { sender, receiver, other } = bucketByGroup(entries);
          const isOpen = openAcc[a.id] ?? idx === 0;
          const missing = countMissing(entries).miss;
          const pcfgEditing = editing && editing.artId === a.id;
          // Dynamic column widths: the more stages there are, the tighter the
          // Parameter, Source and Status columns get, so a four-system
          // landscape (Parameter | Source | 4 stages | Status) still fits
          // without horizontal scroll. The fr shares fill the remaining width
          // evenly; overflow:hidden on .field/.inherit/.p-key/.s-val/.stage-code
          // truncates text to keep it inside its column.
          const dense = stageCount >= 3;
          const paramMin = dense ? 150 : 190;
          const srcMin = dense ? 150 : 200;
          const stageMin = stageCount >= 4 ? 118 : stageCount === 3 ? 140 : 185;
          const statusMin = dense ? 116 : 140;
          const gridCols =
            `minmax(${paramMin}px, ${dense ? 0.9 : 1}fr) minmax(${srcMin}px, 1.15fr) ` +
            Array(stageCount).fill(`minmax(${stageMin}px, 1.1fr)`).join(" ") +
            ` minmax(${statusMin}px, 160px)`;
          const minW = paramMin + srcMin + stageCount * stageMin + statusMin + 8;
          return (
            <div key={a.id} className={"acc" + (isOpen ? " open" : "") + (pcfgEditing ? " editing-open" : "")}>
              <button className="acc-h" onClick={() => { closeEditor(); setOpenAcc((s) => ({ ...s, [a.id]: !isOpen })); }}>
                <span className={"exp-btn" + (isOpen ? " open" : "")}><svg className="ic ic-16"><use href="#ic-chevron-right" /></svg></span>
                <span className="acc-title">
                  {a.name}
                  <span className="art-id">
                    {a.id}
                    {activeBucket?.kennung && <span className="ken">{activeBucket.kennung}</span>}
                    {" · "}{t("st." + (a.status || "identical"))}
                  </span>
                </span>
                {missing > 0
                  ? <span className="st st--crit"><i className="dot" />{t("s4.missing", { n: missing })}</span>
                  : <span className="st st--ok"><i className="dot" />{t("s4.allset")}</span>}
              </button>
              {isOpen && (
                <div className="acc-body">
                  <div className={"pcfg-scroll" + (pcfgEditing ? " editing" : "")}>
                    <div className={"pcfg" + (pcfgEditing ? " editing" : "")} style={{ gridTemplateColumns: gridCols, minWidth: minW }}>
                      {/* Header row */}
                      <div className="pcfg-row pcfg-head" role="row">
                        <div className="pcfg-cell hd">{t("th.param")}</div>
                        <div className="pcfg-cell hd">{quellCode ? `${t("th.src")} (${quellCode})` : t("th.src")}</div>
                        {effektiveStufen.map((st, sidx) => (
                          <div key={st.key} className="pcfg-cell hd stage">
                            <span className="stage-code">{st.code || t("th.tgt")}</span>
                            {stageCount > 1 && (
                              <span className="stage-sub">{sidx === 0 ? t("s4.hdrBasis") : t("s4.hdrOverlay")}</span>
                            )}
                          </div>
                        ))}
                        <div className="pcfg-cell hd">{t("th.status")}</div>
                      </div>

                      {[{ g: "s4.grpSender", list: sender }, { g: "s4.grpReceiver", list: receiver }, { g: "s4.grpGeneral", list: other }].map(({ g, list }, gi) => (
                        list.length > 0 && (
                          <React.Fragment key={g + gi}>
                            <div className="pcfg-grp" role="row"><span>{t(g)}</span> <span className="grp-count">{list.length}</span></div>
                            {list.map((e) => {
                              const isEditingRow = pcfgEditing && e.ParameterKey === editing.paramKey;
                              const rowEditRowCls = isEditingRow ? " editing-row" : "";
                              // Hide dimmed rows (spotlight effect) from screen readers too.
                              const dimHidden = pcfgEditing && !isEditingRow ? true : undefined;
                              const baseReq = classifyConfigStatus(e) === "required";
                              const baseVal = e.newValue || "";
                              const rowSt = computeRowStatus(e, isEditingRow);
                              const baseHasEff = !!effektiverBasisWert(e);
                              return (
                                <div className="pcfg-row" role="row" key={e.ParameterKey}>
                                  <div className={"pcfg-cell p" + rowEditRowCls} aria-hidden={dimHidden}>
                                    <span className="p-key" title={e.ParameterKey}>{e.ParameterKey}</span>
                                    <span className="p-meta">
                                      {baseReq && <span className="req">{t("s4.required")}</span>}
                                      {baseReq ? " · " : ""}{e.DataType || "xsd:string"}
                                    </span>
                                  </div>
                                  <div className={"pcfg-cell s" + rowEditRowCls} title={e.defaultValue || ""} aria-hidden={dimHidden}>
                                    <span className={"s-val" + (e.defaultValue ? "" : " empty")}>{e.defaultValue || "-"}</span>
                                  </div>

                                  {effektiveStufen.map((st, sidx) => {
                                    const isBasis = sidx === 0;
                                    const isEditingCell = isEditingRow && editing.stageKey === st.key;
                                    const hideCls = isEditingCell ? { visibility: "hidden" } : undefined;
                                    let cellInner;
                                    if (isBasis) {
                                      cellInner = (
                                        <div
                                          className={"field" + (baseReq && !baseVal ? " invalid" : "")}
                                          style={hideCls}
                                          role="button"
                                          tabIndex={isEditingCell ? -1 : 0}
                                          aria-label={t("s4.edAriaBasis", { param: e.ParameterKey })}
                                          onClick={() => openEditor(a.id, e, st, sidx)}
                                          onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); openEditor(a.id, e, st, sidx); } }}
                                        >
                                          <span className={"field-txt" + (baseVal ? "" : " placeholder")}>
                                            {baseVal || e.defaultValue || "-"}
                                          </span>
                                          {EDIT_GLYPH}
                                        </div>
                                      );
                                    } else {
                                      const inh = geerbterWert(e, effektiveStufen, sidx);
                                      const ov = e?.byStage?.[st.key]?.newValue || "";
                                      const isOverride = !!ov && ov !== inh.value;
                                      if (!baseHasEff) {
                                        cellInner = (
                                          <div className="inherit disabled" title={t("s4.setBaseFirst")}>
                                            <span className="inherit-txt">{t("s4.inheritEmpty")}</span>
                                          </div>
                                        );
                                      } else if (isOverride) {
                                        cellInner = (
                                          <div
                                            className="field override"
                                            style={hideCls}
                                            role="button"
                                            tabIndex={isEditingCell ? -1 : 0}
                                            aria-label={t("s4.edAriaOverride", { param: e.ParameterKey, stage: st.code })}
                                            title={t("s4.divergeTitle")}
                                            onClick={() => openEditor(a.id, e, st, sidx)}
                                            onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); openEditor(a.id, e, st, sidx); } }}
                                          >
                                            <span className="field-txt">{ov}</span>
                                            <button
                                              type="button"
                                              className="reset-btn"
                                              title={t("s4.edResetTitle")}
                                              aria-label={t("s4.edResetTitle")}
                                              onClick={(ev) => { ev.stopPropagation(); resetOverride(a.id, e.ParameterKey, st.key); }}
                                            >
                                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" /></svg>
                                            </button>
                                          </div>
                                        );
                                      } else {
                                        cellInner = (
                                          <div className="inherit" style={hideCls} onClick={() => openEditor(a.id, e, st, sidx)}>
                                            <span className="inherit-txt" title={t("s4.inherits", { v: inh.value || "-" })}>
                                              {t("s4.inherits", { v: inh.value || "-" })}
                                            </span>
                                            <button
                                              type="button"
                                              className="add-btn"
                                              aria-label={t("s4.edAriaOverride", { param: e.ParameterKey, stage: st.code })}
                                              onClick={(ev) => { ev.stopPropagation(); openEditor(a.id, e, st, sidx); }}
                                            >
                                              <span className="plus">+</span> {t("s4.addOverride")}
                                            </button>
                                          </div>
                                        );
                                      }
                                    }
                                    return (
                                      <div key={st.key} className={"pcfg-cell stage" + rowEditRowCls + (isEditingCell ? " editing-cell" : "")} aria-hidden={dimHidden}>
                                        {cellInner}
                                        {isEditingCell && renderEditor(e, st, sidx)}
                                      </div>
                                    );
                                  })}

                                  <div className={"pcfg-cell stat" + rowEditRowCls} aria-hidden={dimHidden}>
                                    <span className={"st st--" + rowSt.cls}><i className="dot" />{rowSt.label}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </React.Fragment>
                        )
                      ))}
                    </div>
                  </div>
                  {stufen.length > 1 && (
                    <div className="pcfg-hint">{t("s4.stageInheritHint")}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="cred-box" dangerouslySetInnerHTML={{ __html: t("s4.cred") }} />

      <div className="footbar">
        <button className="btn btn-ghost" onClick={() => dispatch({ type: "SET_STEP", payload: 3 })}>
          <svg className="ic ic-16"><use href="#ic-collapse" /></svg> {t("common.back")}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span className="foot-hint">
            {canNext ? t("s4.allset") : t("s4.missing", { n: offen })}
          </span>
          <button className="btn btn-cta" onClick={onWeiter}>
            {t("s4.next")}
            <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
          </button>
        </div>
      </div>

      {/* Warning popup implemented natively as .modal-ov (no MUI portal). */}
      <div
        className={"modal-ov" + (showWarn ? " show" : "")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="s4-warn-title"
        onClick={(e) => { if (e.target === e.currentTarget) setShowWarn(false); }}
      >
        <div className="modal">
          <div className="modal-h">
            <h3 id="s4-warn-title">{t("s4.warnTitle")}</h3>
            <button type="button" className="modal-x" aria-label={t("common.close")} onClick={() => setShowWarn(false)}>
              <svg className="ic ic-16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6 L18 18 M18 6 L6 18" />
              </svg>
            </button>
          </div>
          <div className="modal-b">
            <p style={{ margin: 0, color: "var(--t2)" }}>
              {offen === 1 ? t("s4.warnBodyOne") : t("s4.warnBody", { n: offen })}
            </p>
          </div>
          <div className="modal-h" style={{ borderTop: "1px solid var(--border-sub)", borderBottom: "none", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="btn btn-sec" onClick={() => setShowWarn(false)}>
              {t("s4.warnCancel")}
            </button>
            <button type="button" className="btn btn-cta" onClick={bestaetigeWeiter}>
              {t("s4.warnGo")}
              <span className="btn-arrow"><svg className="ic"><use href="#ic-arrow-right" /></svg></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
